export const SENTRY_ORG = "tw-ai-consulting";
export const SYNTHETIC_TRAFFIC_HEADER = "x-alexai-traffic";
export const SYNTHETIC_TRAFFIC_VALUE = "synthetic";

export const SENTRY_SERVICES = {
  "alexai-pro-web": {
    railwayService: "AlexAI Pro",
    project: "alexai-pro-web",
    runtimeDsnVariable: "NEXT_PUBLIC_SENTRY_DSN",
    platform: "nextjs",
  },
  "alexai-pro-api": {
    railwayService: "AlexAI Pro API",
    project: "alexai-pro-api",
    runtimeDsnVariable: "SENTRY_DSN",
    platform: "python",
  },
  "alexai-gov-web": {
    railwayService: "AlexAI Gov",
    project: "alexai-gov-web",
    runtimeDsnVariable: "NEXT_PUBLIC_SENTRY_DSN",
    platform: "nextjs",
  },
  "alexai-gov-api": {
    railwayService: "AlexAI Gov API",
    project: "alexai-gov-api",
    runtimeDsnVariable: "SENTRY_DSN",
    platform: "python",
  },
  alexai: {
    railwayService: "AlexAI",
    project: "alexai",
    runtimeDsnVariable: "NEXT_PUBLIC_SENTRY_DSN",
    platform: "nextjs",
  },
  energenius: {
    railwayService: "Energenius",
    project: "energenius",
    runtimeDsnVariable: "NEXT_PUBLIC_SENTRY_DSN",
    platform: "nextjs",
  },
  "knowledge-base": {
    railwayService: "Knowledge Base",
    project: "knowledge-base",
    runtimeDsnVariable: "NEXT_PUBLIC_SENTRY_DSN",
    platform: "nextjs",
  },
  users: {
    railwayService: "Users",
    project: "users",
    runtimeDsnVariable: "NEXT_PUBLIC_SENTRY_DSN",
    platform: "nextjs",
  },
  "etp-search": {
    railwayService: "etp-search",
    project: "etp-search",
    runtimeDsnVariable: "NEXT_PUBLIC_SENTRY_DSN",
    platform: "nextjs",
  },
  "etp-member-portal": {
    railwayService: "ETP Member Portal",
    project: "etp-member-portal",
    runtimeDsnVariable: "VITE_SENTRY_DSN",
    platform: "vite",
  },
} as const;

export type SentryService = keyof typeof SENTRY_SERVICES;
export type RuntimeEnvironment = "local" | "staging" | "production";
export type Environment = Record<string, string | undefined>;
type SentryHeaders = Record<string, unknown> | Array<[string, unknown]>;
type SentryEvent = {
  request?: {
    headers?: SentryHeaders;
  };
  tags?: Record<string, unknown>;
};
type BeforeSend = (event: SentryEvent, hint: unknown) => unknown;

export const SENTRY_INIT_DEFAULTS = {
  sendDefaultPii: true,
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
} as const;

export function resolveProSentryService(deploymentMode: unknown): SentryService {
  return normalized(deploymentMode) === "gov"
    ? "alexai-gov-web"
    : "alexai-pro-web";
}

function processEnvironment(): Environment {
  return typeof process === "undefined" ? {} : process.env;
}

function normalized(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function normalizeCanonicalEnvironment(value: unknown): RuntimeEnvironment | undefined {
  const candidate = normalized(value);
  if (candidate === "production" || candidate === "prod") {
    return "production";
  }
  if (candidate === "staging" || candidate === "stage") {
    return "staging";
  }
  if (candidate === "local" || candidate === "development" || candidate === "dev") {
    return "local";
  }
  return undefined;
}

export function resolveRuntimeEnvironment(
  env: Environment = processEnvironment(),
): RuntimeEnvironment {
  const railwayName = normalized(env.RAILWAY_ENVIRONMENT_NAME);
  if (railwayName) {
    return railwayName === "production" || railwayName === "prod"
      ? "production"
      : "staging";
  }

  const explicit =
    normalizeCanonicalEnvironment(env.NEXT_PUBLIC_SENTRY_ENVIRONMENT) ??
    normalizeCanonicalEnvironment(env.VITE_SENTRY_ENVIRONMENT) ??
    normalizeCanonicalEnvironment(env.SENTRY_ENVIRONMENT);
  if (explicit) {
    return explicit;
  }

  if (env.RAILWAY_ENVIRONMENT || env.RAILWAY_SERVICE_NAME || env.RAILWAY_PROJECT_ID) {
    return "staging";
  }

  return normalizeCanonicalEnvironment(env.ENVIRONMENT) ?? "local";
}

function validCommit(value: unknown): string | undefined {
  const candidate = normalized(value);
  return /^[0-9a-f]{7,64}$/.test(candidate) ? candidate : undefined;
}

function validRelease(service: SentryService, value: unknown): string | undefined {
  if (typeof value !== "string" || !value.startsWith(`${service}@`)) {
    return undefined;
  }
  const commit = validCommit(value.slice(service.length + 1));
  return commit ? `${service}@${commit}` : undefined;
}

export function resolveSentryRelease(
  service: SentryService,
  env: Environment = processEnvironment(),
): string | undefined {
  const railwayCommit = validCommit(env.RAILWAY_GIT_COMMIT_SHA);
  if (railwayCommit) {
    return `${service}@${railwayCommit}`;
  }

  const publicRelease = validRelease(
    service,
    env.NEXT_PUBLIC_SENTRY_RELEASE ?? env.VITE_SENTRY_RELEASE,
  );
  if (publicRelease) {
    return publicRelease;
  }

  const fallbackCommit =
    validCommit(env.SOURCE_COMMIT) ??
    validCommit(env.GITHUB_SHA);
  return fallbackCommit ? `${service}@${fallbackCommit}` : undefined;
}

export function buildNextConfigEnv(
  service: SentryService,
  env: Environment = processEnvironment(),
): Record<string, string> {
  const release = resolveSentryRelease(service, env);
  return {
    NEXT_PUBLIC_SENTRY_ENVIRONMENT: resolveRuntimeEnvironment(env),
    ...(release ? { NEXT_PUBLIC_SENTRY_RELEASE: release } : {}),
  };
}

function headerValue(
  headers: SentryHeaders | undefined,
  wanted: string,
): string | undefined {
  if (Array.isArray(headers)) {
    const pair = headers.find(([name]) => normalized(name) === wanted);
    return pair ? normalized(pair[1]) : undefined;
  }
  if (!headers || typeof headers !== "object") {
    return undefined;
  }
  const entry = Object.entries(headers).find(([name]) => normalized(name) === wanted);
  return entry ? normalized(entry[1]) : undefined;
}

function browserTrafficIsSynthetic(): boolean {
  const runtime = globalThis as typeof globalThis & {
    __ALEXAI_TRAFFIC__?: string;
    document?: { cookie?: string };
  };
  if (normalized(runtime.__ALEXAI_TRAFFIC__) === SYNTHETIC_TRAFFIC_VALUE) {
    return true;
  }
  return (runtime.document?.cookie ?? "")
    .split(";")
    .some((cookie) => cookie.trim() === "alexai_traffic=synthetic");
}

export function tagSentryEvent<T extends SentryEvent>(
  event: T,
  service: SentryService,
): T {
  const requestIsSynthetic =
    headerValue(event.request?.headers, SYNTHETIC_TRAFFIC_HEADER) ===
    SYNTHETIC_TRAFFIC_VALUE;
  event.tags = {
    ...event.tags,
    service,
    traffic: requestIsSynthetic || browserTrafficIsSynthetic() ? "synthetic" : "user",
  };
  return event;
}

export function buildInitOptions<T extends Record<string, unknown>>(
  service: SentryService,
  appOptions: T,
): typeof SENTRY_INIT_DEFAULTS & T & Record<string, unknown> {
  const environment =
    normalizeCanonicalEnvironment(appOptions.environment) ??
    resolveRuntimeEnvironment();
  const release =
    validRelease(service, appOptions.release) ||
    resolveSentryRelease(service);
  const enabled =
    typeof appOptions.enabled === "boolean"
      ? appOptions.enabled
      : environment !== "local";
  const beforeSend =
    typeof appOptions.beforeSend === "function"
      ? (appOptions.beforeSend as BeforeSend)
      : undefined;
  const beforeSendTransaction =
    typeof appOptions.beforeSendTransaction === "function"
      ? (appOptions.beforeSendTransaction as BeforeSend)
      : undefined;
  const initialScope =
    appOptions.initialScope &&
    typeof appOptions.initialScope === "object" &&
    !Array.isArray(appOptions.initialScope)
      ? (appOptions.initialScope as Record<string, unknown>)
      : {};
  const initialTags =
    initialScope.tags &&
    typeof initialScope.tags === "object" &&
    !Array.isArray(initialScope.tags)
      ? (initialScope.tags as Record<string, unknown>)
      : {};

  return {
    ...SENTRY_INIT_DEFAULTS,
    ...appOptions,
    environment,
    release,
    enabled,
    initialScope: {
      ...initialScope,
      tags: {
        ...initialTags,
        service,
        traffic: browserTrafficIsSynthetic() ? "synthetic" : "user",
      },
    },
    beforeSend: (event: SentryEvent, hint: unknown) => {
      const tagged = tagSentryEvent(event, service);
      return beforeSend ? beforeSend(tagged, hint) : tagged;
    },
    beforeSendTransaction: (event: SentryEvent, hint: unknown) => {
      const tagged = tagSentryEvent(event, service);
      return beforeSendTransaction ? beforeSendTransaction(tagged, hint) : tagged;
    },
  };
}

export function isSentryClientVerificationEnabled(value: unknown): boolean {
  return normalized(value) === "true";
}

export function installSentryVerificationHarness(
  service: SentryService,
  enabled = false,
): boolean {
  const runtime = globalThis as typeof globalThis & {
    __ALEXAI_TRAFFIC__?: string;
    __alexaiSentryVerify?: () => void;
  };

  if (enabled !== true) {
    delete runtime.__alexaiSentryVerify;
    return false;
  }
  if (typeof runtime.document === "undefined" || runtime.__alexaiSentryVerify) {
    return false;
  }
  Object.defineProperty(runtime, "__alexaiSentryVerify", {
    configurable: true,
    value: () => {
      runtime.__ALEXAI_TRAFFIC__ = SYNTHETIC_TRAFFIC_VALUE;
      globalThis.setTimeout(() => {
        throw sentryVerificationError(service);
      }, 0);
    },
  });
  return true;
}

function constantTimeEqual(left: string, right: string): boolean {
  if (left.length !== right.length) {
    return false;
  }
  let mismatch = 0;
  for (let index = 0; index < left.length; index += 1) {
    mismatch |= left.charCodeAt(index) ^ right.charCodeAt(index);
  }
  return mismatch === 0;
}

export function authorizeSentryVerification(
  headers: { get(name: string): string | null },
  configuredToken: string | undefined,
): boolean {
  const suppliedToken = headers.get("x-sentry-verification-token") ?? "";
  return Boolean(
    configuredToken &&
    suppliedToken &&
    constantTimeEqual(configuredToken, suppliedToken),
  );
}

export function sentryVerificationError(service: SentryService): Error {
  return new Error(`Sentry verification error for ${service}`);
}

type ServerSentrySendEvent = {
  event_id?: string;
};

type ServerSentrySendResponse = {
  statusCode?: number;
};

type ServerSentryClient = {
  getOptions(): unknown;
  on(
    hook: "afterSendEvent",
    callback: (
      event: ServerSentrySendEvent,
      response: ServerSentrySendResponse,
    ) => void,
  ): () => void;
};

type ServerSentrySdk = {
  getClient(): ServerSentryClient | undefined;
  captureException(exception: unknown): string;
  flush(timeout?: number): Promise<boolean>;
};

export type ServerSentryVerificationFailureReason =
  | "sdk-uninitialized"
  | "sdk-identity-mismatch"
  | "capture-failed"
  | "flush-failed"
  | "delivery-failed";

export type ServerSentryVerificationResult =
  | {
      captured: true;
      flushed: true;
      accepted: true;
      service: SentryService;
      platform: "node";
      environment: RuntimeEnvironment;
      release: string;
    }
  | {
      captured: boolean;
      flushed: boolean;
      accepted: false;
      service: SentryService;
      reason: ServerSentryVerificationFailureReason;
    };

export type ServerSentryVerificationOptions = {
  env?: Environment;
  flushTimeout?: number;
};

function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function serverVerificationFailure(
  service: SentryService,
  reason: ServerSentryVerificationFailureReason,
  captured = false,
  flushed = false,
): ServerSentryVerificationResult {
  return {
    captured,
    flushed,
    accepted: false,
    service,
    reason,
  };
}

/**
 * Capture a token-authorized verification event only after confirming that the
 * active SDK is the intended Node client. The result never exposes a DSN and
 * reports success only when Sentry accepts an event ID and drains its buffer.
 */
export async function captureServerSentryVerification(
  sdk: ServerSentrySdk,
  service: SentryService,
  options: ServerSentryVerificationOptions = {},
): Promise<ServerSentryVerificationResult> {
  const env = options.env ?? processEnvironment();
  const environment = resolveRuntimeEnvironment(env);
  const release = resolveSentryRelease(service, env);
  if (
    typeof sdk.getClient !== "function" ||
    typeof sdk.captureException !== "function" ||
    typeof sdk.flush !== "function"
  ) {
    return serverVerificationFailure(service, "sdk-uninitialized");
  }
  const client = sdk.getClient();
  if (!client) {
    return serverVerificationFailure(service, "sdk-uninitialized");
  }

  let clientOptions: Record<string, unknown> | undefined;
  try {
    clientOptions = record(client.getOptions());
  } catch {
    clientOptions = undefined;
  }
  const initialScope = record(clientOptions?.initialScope);
  const tags = record(initialScope?.tags);
  if (
    clientOptions?.platform !== "node" ||
    clientOptions.enabled !== true ||
    typeof clientOptions.dsn !== "string" ||
    clientOptions.dsn.trim() === "" ||
    clientOptions.environment !== environment ||
    !release ||
    clientOptions.release !== release ||
    tags?.service !== service ||
    typeof client.on !== "function"
  ) {
    return serverVerificationFailure(service, "sdk-identity-mismatch");
  }

  const sendStatuses = new Map<string, number | undefined>();
  let removeAfterSend: () => void;
  try {
    removeAfterSend = client.on("afterSendEvent", (event, response) => {
      if (event.event_id) {
        sendStatuses.set(event.event_id, response.statusCode);
      }
    });
  } catch {
    return serverVerificationFailure(service, "sdk-identity-mismatch");
  }

  let eventId: string;
  try {
    eventId = sdk.captureException(sentryVerificationError(service));
  } catch {
    removeAfterSend();
    return serverVerificationFailure(service, "capture-failed");
  }
  if (!eventId) {
    removeAfterSend();
    return serverVerificationFailure(service, "capture-failed");
  }

  let flushed = false;
  try {
    flushed = await sdk.flush(options.flushTimeout ?? 2_000);
  } catch {
    flushed = false;
  }
  removeAfterSend();
  if (!flushed) {
    return serverVerificationFailure(service, "flush-failed", true);
  }

  const statusCode = sendStatuses.get(eventId);
  if (!statusCode || statusCode < 200 || statusCode >= 300) {
    return serverVerificationFailure(service, "delivery-failed", true, true);
  }

  return {
    captured: true,
    flushed: true,
    accepted: true,
    service,
    platform: "node",
    environment,
    release,
  };
}

export function buildPluginOptions(
  project: SentryService,
  overrides?: Record<string, unknown>,
) {
  if (!SENTRY_SERVICES[project]) {
    throw new Error(`Unknown Sentry project slug: ${project}`);
  }
  const release = resolveSentryRelease(project);
  return {
    org: processEnvironment().SENTRY_ORG ?? SENTRY_ORG,
    project,
    authToken: processEnvironment().SENTRY_AUTH_TOKEN,
    ...(release ? { release: { name: release } } : {}),
    ...overrides,
  };
}
